export type SearchLevel = "h2" | "olevel" | "ip";
export type SearchSubject = "physics" | "chemistry" | "biology" | "maths";

export const H2_LEVEL_TOKENS = new Set(["h2", "jc", "alevel"]);
export const OLEVEL_TOKENS = new Set(["olevel", "secondary", "sec"]);
export const IP_LEVEL_TOKENS = new Set(["ip", "integrated", "programme"]);
export const JC_INTENT_TOKENS = new Set(["jc", "junior", "college"]);
export const ALEVEL_INTENT_TOKENS = new Set(["alevel", "h2", "h1", "jc"]);
export const H2_INTENT_TOKENS = new Set(["h2", "alevel"]);
export const OLEVEL_INTENT_TOKENS = OLEVEL_TOKENS;
export const IP_INTENT_TOKENS = IP_LEVEL_TOKENS;

export const PRACTICAL_INTENT_TOKENS = new Set([
  "practical",
  "experiment",
  "experimnt",
  "lab",
]);
export const NOTES_INTENT_TOKENS = new Set(["note", "notes", "syllabus"]);
export const SCHOLARSHIP_INTENT_TOKENS = new Set([
  "scholarship",
  "scholarships",
  "bond",
]);
export const BURSARY_INTENT_TOKENS = new Set([
  "bursary",
  "bursaries",
  "financial",
  "aid",
]);
export const TUITION_INTENT_TOKENS = new Set([
  "tuition",
  "class",
  "classes",
  "coach",
  "coaching",
]);
export const BOND_INTENT_TOKENS = new Set(["bond", "bonded", "bondfree"]);
export const CHEMISTRY_INTENT_TOKENS = new Set(["chemistry", "chem"]);
export const BIOLOGY_INTENT_TOKENS = new Set(["biology", "bio"]);
export const PHYSICS_INTENT_TOKENS = new Set(["physics", "phys"]);
export const MATHS_INTENT_TOKENS = new Set(["maths", "math", "mathematics"]);
export const CALENDAR_INTENT_TOKENS = new Set([
  "calendar",
  "term",
  "terms",
  "holiday",
  "holidays",
  "public",
  "schedule",
  "dates",
  "date",
  "march",
  "june",
  "september",
  "year",
  "end",
]);

export const SUBJECT_TOKEN_ALIASES: Readonly<
  Record<SearchSubject, readonly string[]>
> = {
  physics: ["physics", "phys"],
  chemistry: ["chemistry", "chem"],
  biology: ["biology", "bio"],
  maths: ["maths", "math", "mathematics"],
};

export const ALL_SUBJECT_TOKENS = new Set(Object.values(SUBJECT_TOKEN_ALIASES).flat());

export const INTENT_PRIORITY_TOKENS = new Set([
  "practical",
  "experiment",
  "lab",
  "notes",
  "scholarship",
  "scholarships",
  "bursary",
  "bursaries",
  "tuition",
  "bond",
  "calendar",
  "holiday",
  "holidays",
]);

export const PRACTICAL_SPECIALIST_TOKENS = new Set([
  "calorimetry",
  "kinetic",
  "kinetics",
  "volumetric",
  "electrolysis",
  "redox",
  "titration",
  "analysis",
  "catalase",
  "enzyme",
  "ecology",
  "photosynthesis",
  "respiration",
  "uncertainty",
  "pdo",
  "paper",
]);

export const PRACTICAL_RERANK_SPECIALIST_TOKENS = [
  "calorimetry",
  "kinetic",
  "kinetics",
  "volumetric",
  "electrolysis",
  "redox",
  "titration",
  "catalase",
  "enzyme",
  "ecology",
  "photosynthesis",
  "respiration",
  "pdo",
  "uncertainty",
  "investigation",
  "investigations",
  "stoichiometry",
] as const;

export function detectSearchLevel(tokens: readonly string[]): SearchLevel | null {
  const tokenSet = new Set(tokens);
  if ([...H2_LEVEL_TOKENS].some((token) => tokenSet.has(token))) return "h2";
  if ([...OLEVEL_TOKENS].some((token) => tokenSet.has(token))) return "olevel";
  if ([...IP_LEVEL_TOKENS].some((token) => tokenSet.has(token))) return "ip";
  return null;
}

export function detectSearchSubject(
  tokens: readonly string[]
): SearchSubject | null {
  const tokenSet = new Set(tokens);
  const subjects: SearchSubject[] = ["physics", "chemistry", "biology", "maths"];
  for (const subject of subjects) {
    if (SUBJECT_TOKEN_ALIASES[subject].some((alias) => tokenSet.has(alias))) {
      return subject;
    }
  }
  return null;
}

export function levelToQueryPhrase(level: SearchLevel): string {
  if (level === "olevel") return "o level";
  return level;
}
